import type { ModuleServiceSchemas, OrchestratorSchemas } from '@ctcells/contract'

import { orchestrator } from '../clients.ts'
import { keys } from '../keys.ts'
import { createQuery, type SkippableArgs } from '../query.ts'

/**
 * 위치 등록부 · 모듈 위치 티칭 조회 — `IFC-RO-95` · `97` · `98` · `102`(v0.9.4).
 *
 * 실물(`ctcells-core/app/views/settings_positions.py` · `views/modules.py` 「코어 티칭」 절 · core@d50b8cf4 · dev2 2026-10-12 실측)
 *
 * | 경로 | 역할 | 응답 | dev2 실측 |
 * | --- | --- | --- | --- |
 * | `GET /settings/positions` | **전원**(`ROLES`) | `PositionRegistryList` — `items` · `fields`(고칠 수 있는 칸) · `choices`(서버가 정한 선택지) | 50 지점 · 보호 12 · 비활성 0 |
 * | `GET /modules/{id}/positions` | 전원 | `{ items: Position[](모듈 계약 그대로), teach: TeachGate }` — 가드가 닫혀도 읽는다 | `centrifuge-1` 10 지점 · `teach.open=false`(`system_mode`) |
 * | `GET /modules/{id}/pose` | **operator · admin**(`WRITE_ROLES`) | 모듈 `ModulePose` 그대로 — 🔴 **가드가 닫히면 409 `TEACH_NOT_ALLOWED`** | 409(운영 모드 `normal`) |
 * | `GET /modules/{id}/positions/{pid}/resolve` | 전원 | `PositionResolution` — 쿼리 조합은 셋(없음 · `recipe_id` 만 · 셋 다). 그 밖은 422 · 그 모듈이 모르는 지점은 404 `MODULE_POSITION_NOT_OWNED` | `centrifuge.safe_position` 200 |
 *
 * - 🔴 **모듈 위치 · 자세는 `keys.modules` 밖에 둔다**(`keys.positions`). 셋 다 **모듈에 HTTP 를 중계**한다 —
 *   `modules.all()`(모듈 상태 프레임마다) 아래면 배치 중 모듈 사건마다 모듈에 다시 쏜다.
 *   그래서 그 모듈의 `version` 을 키에 싣는다 — 그 모듈이 바뀌면(상태 · 제어 주체 = 가드의 두 축) 한 번 받는다.
 * - 🔴 주기 폴링 없다. 자세는 화면이 «진입 때 · 조그 뒤 한 번»(서버 설명문) — 조그 응답이 곧 움직인 뒤 자세라 그 값을 쓴다.
 * - ⚠ 가드(`teach.open` · `reasons`)는 **서버 판정 그대로** 그린다. 화면이 운영 모드 · 제어 주체로 다시 판정하지 않는다.
 */
export type PositionRegistryItem = OrchestratorSchemas['schemas']['PositionRegistryItem']
export type PositionRegistryListItem = OrchestratorSchemas['schemas']['PositionRegistryListItem']
export type PositionRegistryList = OrchestratorSchemas['schemas']['PositionRegistryList']
export type PositionRegistryWrite = OrchestratorSchemas['schemas']['PositionRegistryWrite']
export type TeachGate = OrchestratorSchemas['schemas']['TeachGate']
export type PositionResolution = OrchestratorSchemas['schemas']['PositionResolution']
/** 모듈 위치 테이블의 한 행 — 모듈 계약 `Position`(L2 는 고치지 않고 중계한다) */
export type ModulePosition = ModuleServiceSchemas['schemas']['Position']
/** 모듈이 읽은 지금 자세 — 모듈 계약 `ModulePose`(조그 응답도 이 모양이다) */
export type ModulePoseReading = ModuleServiceSchemas['schemas']['ModulePose']

/**
 * `GET /modules/{id}/positions` 응답. 계약 `ModulePositionList.items` 는 `{[k]: unknown}[]` 라
 * 모듈 계약 `Position` 으로 좁혀 둔다(서버가 「모듈 계약 `Position` 그대로」라 적는다).
 */
export interface ModulePositionList {
  items: ModulePosition[]
  teach: TeachGate
}

/**
 * 등록부 `choices` — 계약은 `{[k]: unknown}` 이지만 서버(`position_registry_admin.choices()`)가 짓는 모양은 이것이다.
 * ⚠ 화면은 이 값을 펼치기만 한다. 판정(정합 · 보호 · 사용 중)은 저장 때 서버가 다시 한다.
 */
export interface PositionRegistryChoices {
  kind?: string[]
  dof_of_kind?: Record<string, string>
  owner?: string[]
  frame?: string[]
  approach?: string[]
  labware_rotation_deg?: number[]
  /** 소유 모듈 → 부가축 값 이름(`lift_mm` …) */
  axes?: Record<string, string[]>
  slide_axis?: string[]
}

export const usePositionRegistry = createQuery<SkippableArgs | void, PositionRegistryList>({
  key: () => keys.positions.registry(),
  fetch: () =>
    orchestrator.request<PositionRegistryList>({
      method: 'GET',
      path: '/settings/positions',
      retryOn401: true,
    }),
})

export interface ModulePositionsArgs extends SkippableArgs {
  moduleId: string
  /** 그 모듈 스냅샷의 `version` — 바뀌면 키가 바뀌어 한 번 다시 받는다(가드의 상태 · 제어 주체 축) */
  version?: number | undefined
}

export const useModulePositions = createQuery<ModulePositionsArgs, ModulePositionList>({
  key: ({ moduleId, version }) => keys.positions.module(moduleId, version),
  fetch: ({ moduleId }) =>
    orchestrator.request<ModulePositionList>({
      method: 'GET',
      path: `/modules/${encodeURIComponent(moduleId)}/positions`,
      retryOn401: true,
    }),
})

/**
 * 지금 자세 — 🔴 **가드가 열렸을 때만 부른다**(`skip`). 닫혔으면 서버가 409 를 줄 요청이다.
 * operator · admin 전용이라 viewer 도 `skip` 이다(컨테이너가 가른다).
 */
export const useModulePose = createQuery<SkippableArgs & { moduleId: string }, ModulePoseReading>({
  key: ({ moduleId }) => keys.positions.pose(moduleId),
  fetch: ({ moduleId }) =>
    orchestrator.request<ModulePoseReading>({
      method: 'GET',
      path: `/modules/${encodeURIComponent(moduleId)}/pose`,
      retryOn401: true,
    }),
})

export interface PositionResolveArgs extends SkippableArgs {
  moduleId: string
  positionId: string | undefined
  /** 쿼리 ⓑ(지점 + 그 레시피의 랩웨어 후보) · ⓒ(셋 다 · 웰). 🔴 셋 중 일부만은 422 다 — 화면이 조합을 지킨다 */
  recipeId?: string | undefined
  labwareId?: string | undefined
  well?: string | undefined
}

/** 쿼리 조합을 서버 규칙(없음 · recipe_id 만 · 셋 다)으로 접는다 — 그 밖의 조합은 recipe_id 만으로 줄인다 */
export function resolveQuery(args: PositionResolveArgs): Record<string, string> {
  const { recipeId, labwareId, well } = args
  if (recipeId === undefined || recipeId === '') return {}
  if (labwareId !== undefined && labwareId !== '' && well !== undefined && well !== '')
    return { recipe_id: recipeId, labware_id: labwareId, well }
  return { recipe_id: recipeId }
}

export const usePositionResolve = createQuery<PositionResolveArgs, PositionResolution>({
  key: (args) => keys.positions.resolve(args.moduleId, args.positionId ?? '', resolveQuery(args)),
  fetch: (args) =>
    orchestrator.request<PositionResolution>({
      method: 'GET',
      path: `/modules/${encodeURIComponent(args.moduleId)}/positions/${encodeURIComponent(
        String(args.positionId),
      )}/resolve`,
      query: resolveQuery(args),
      retryOn401: true,
    }),
  enabled: (args) => args.positionId !== undefined && args.positionId !== '',
})
